'use client';

import type { Configuration, Middleware } from '@repo/api-gw-sdk';
import { createContext, useMemo, type PropsWithChildren } from 'react';

import { nonProjectDalFactory } from '@/data/dal/dalFactory';
import {
  createHttpClient,
  refreshTokenIfNeeded,
  type HttpClient,
} from '@/data/dal/httpClient';

import { createConfig } from './api-config';
import type { Environment } from './environment';
import { useEnvironment } from './useEnvironment';

const createMiddleware = (
  authToken: string,
  updateAuthToken: (token: string) => void,
  environment: Environment
): Middleware => {
  const useRefreshToken = !(
    environment.isDemo ||
    environment.isDev ||
    environment.isTest
  );

  let refreshPromise = Promise.resolve(authToken);
  let isRefreshing = false;

  const refreshToken = () => {
    if (!useRefreshToken || isRefreshing) {
      return refreshPromise;
    }

    isRefreshing = true;
    refreshPromise = refreshTokenIfNeeded(
      authToken,
      environment.clientApiBaseUrl,
      updateAuthToken
    ).then(
      (token) => {
        isRefreshing = false;
        return token;
      },
      () => {
        isRefreshing = false;
        return authToken;
      }
    );
    return refreshPromise;
  };

  return {
    pre: async (context) => {
      if (!context.getUrl().endsWith('/refresh')) {
        const newAuthToken = await refreshToken();
        context.setHeaderParam('Authorization', `Bearer ${newAuthToken}`);
      }
      return context;
    },
    post: async (context) => {
      if (useRefreshToken && context.httpStatusCode === 401) {
        // The user is not authenticated, delete the next jwt and redirect to login
        location.href = '/logout';
      }
      return context;
    },
  };
};

export const NonProjectDalContext = createContext<{
  nonProjectDal: ReturnType<typeof nonProjectDalFactory>;
  httpClient: HttpClient;
  configuration: Configuration;
} | null>(null);

export const NonProjectDalProvider = ({ children }: PropsWithChildren) => {
  const env = useEnvironment();
  const { authToken, updateAuthToken } = env;

  const configuration = useMemo(() => {
    const config = createConfig(env.clientApiBaseUrl, authToken);
    config.middleware!.push(createMiddleware(authToken, updateAuthToken, env));
    return config;
  }, [authToken, env, updateAuthToken]);

  const httpClient = useMemo(
    () => createHttpClient(authToken, updateAuthToken, env),
    [authToken, updateAuthToken, env]
  );

  const nonProjectDal = useMemo(
    () =>
      nonProjectDalFactory(httpClient, configuration, env.isDev || env.isTest),
    [configuration, httpClient, env]
  );

  return (
    <NonProjectDalContext.Provider
      value={{ nonProjectDal, httpClient, configuration }}
    >
      {children}
    </NonProjectDalContext.Provider>
  );
};
